import { useEffect, useMemo, useState } from "react";
import { AppShell } from "@/components/AppShell";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Tabs, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogDescription } from "@/components/ui/dialog";
import { Badge } from "@/components/ui/badge";
import { http } from "@/lib/api";
import { toast } from "sonner";
import { ClipboardEdit, Save, Users, TrendingUp } from "lucide-react";

export default function SchoolAdminDashboard() {
  const [students, setStudents] = useState([]);
  const [metrics, setMetrics] = useState([]);
  const [selected, setSelected] = useState(null);
  const [term, setTerm] = useState(1);
  const [values, setValues] = useState({});
  const [remarks, setRemarks] = useState("");
  const [teacherSign, setTeacherSign] = useState("");
  const [doctorSign, setDoctorSign] = useState("");
  const [classAvg, setClassAvg] = useState({});
  const [open, setOpen] = useState(false);

  const load = async () => {
    try {
      const [s, m] = await Promise.all([http.get("/students"), http.get("/metrics")]);
      setStudents(s.data); setMetrics(m.data);
    } catch { toast.error("Failed to load"); }
  };

  useEffect(() => { load(); }, []);

  const loadTerm = async (student, t) => {
    try {
      const r = await http.get(`/report/${student.id}`);
      const term = r.data.terms.find((x) => x.term === t) || { values: {}, remarks: "", teacher_sign: "", doctor_sign: "" };
      const stVals = {};
      const cAvg = {};
      Object.entries(term.values || {}).forEach(([mid, v]) => {
        if (v && typeof v === "object") {
          if (v.student != null) stVals[mid] = v.student;
          if (v.class_avg != null) cAvg[mid] = v.class_avg;
        } else if (v != null) {
          stVals[mid] = v;
        }
      });
      setValues(stVals);
      setClassAvg(cAvg);
      setRemarks(term.remarks || "");
      setTeacherSign(term.teacher_sign || "");
      setDoctorSign(term.doctor_sign || "");
    } catch { /* noop */ }
  };

  const openEditor = async (student) => {
    setSelected(student); setOpen(true); setTerm(1);
    await loadTerm(student, 1);
  };

  const changeTerm = async (t) => { setTerm(t); if (selected) await loadTerm(selected, t); };

  const setVal = (mid, v) => {
    const n = v === "" ? "" : Number(v);
    setValues((prev) => ({ ...prev, [mid]: Number.isNaN(n) ? v : n }));
  };

  const save = async () => {
    try {
      await http.post("/screenings", {
        student_id: selected.id,
        term,
        date: new Date().toISOString().slice(0, 10),
        values,
        remarks,
        teacher_sign: teacherSign,
        doctor_sign: doctorSign,
        parent_sign: "",
      });
      toast.success(`Term ${term} saved for ${selected.name}. Class average will auto-update.`);
      setOpen(false); load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Save failed");
    }
  };

  const grouped = useMemo(() => {
    const map = {};
    metrics.forEach((m) => { (map[m.category] = map[m.category] || []).push(m); });
    return Object.entries(map);
  }, [metrics]);

  return (
    <AppShell
      subtitle="School Admin · data entry"
      title="Enter screening data"
      right={<Badge variant="outline" className="text-xs"><Users className="w-3 h-3 mr-1" /> {students.length} students</Badge>}
    >
      <Card>
        <CardHeader>
          <CardTitle className="font-heading text-lg">Students in your school</CardTitle>
          <div className="text-sm text-slate-500 flex items-center gap-2 mt-1">
            <TrendingUp className="w-4 h-4 text-sky-600" />
            Enter only the student value for each metric. Class average is computed automatically across classmates in the same term.
          </div>
        </CardHeader>
        <CardContent>
          <div className="overflow-x-auto">
            <table className="w-full text-sm" data-testid="schooladmin-students-table">
              <thead className="text-slate-400 uppercase text-[10px] tracking-widest">
                <tr>
                  <th className="text-left py-2 px-2">KID ID</th>
                  <th className="text-left py-2 px-2">Name</th>
                  <th className="text-left py-2 px-2">Class</th>
                  <th className="text-left py-2 px-2">Level</th>
                  <th className="text-right py-2 px-2">Action</th>
                </tr>
              </thead>
              <tbody>
                {students.map((s) => (
                  <tr key={s.id} className="border-t border-slate-100 hover:bg-slate-50/60">
                    <td className="py-2.5 px-2 font-mono text-slate-500">{s.kid_id}</td>
                    <td className="py-2.5 px-2 font-semibold text-slate-800">{s.name}</td>
                    <td className="py-2.5 px-2 text-slate-600">{s.grade}{s.section}</td>
                    <td className="py-2.5 px-2">
                      <Badge style={{ background: s.gamification?.current?.color, color: "white" }}>
                        Lv {s.gamification?.current?.level} · {s.gamification?.current?.name}
                      </Badge>
                    </td>
                    <td className="py-2.5 px-2 text-right">
                      <Button size="sm" data-testid={`enter-screening-${s.id}`} onClick={() => openEditor(s)} className="bg-sky-500 hover:bg-sky-600">
                        <ClipboardEdit className="w-4 h-4 mr-1" /> Enter screening
                      </Button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </CardContent>
      </Card>

      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="max-w-4xl max-h-[85vh] overflow-y-auto" data-testid="screening-editor-dialog">
          <DialogHeader>
            <DialogTitle className="font-heading">{selected?.name} · Screening entry</DialogTitle>
            <DialogDescription>
              <span className="font-mono">{selected?.kid_id}</span> · Class {selected?.grade}{selected?.section}
            </DialogDescription>
          </DialogHeader>
          <Tabs value={String(term)} onValueChange={(v) => changeTerm(Number(v))}>
            <TabsList data-testid="term-tabs">
              <TabsTrigger value="1" data-testid="term-1-tab">Term 1</TabsTrigger>
              <TabsTrigger value="2" data-testid="term-2-tab">Term 2</TabsTrigger>
            </TabsList>
          </Tabs>

          <div className="space-y-6 mt-4">
            {grouped.map(([cat, mets]) => (
              <div key={cat}>
                <div className="text-xs uppercase tracking-widest font-bold text-sky-700 mb-2">{cat}</div>
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                  {mets.map((m) => (
                    <div key={m.id} className="border border-slate-200 rounded-lg p-3">
                      <div className="flex justify-between items-baseline mb-2">
                        <div className="font-semibold text-slate-800 text-sm">{m.name}</div>
                        <div className="text-[10px] uppercase tracking-widest text-slate-400">
                          {m.unit || m.who_target}
                        </div>
                      </div>
                      <div className="flex items-end gap-3">
                        <div className="flex-1">
                          <Label className="text-[10px] uppercase tracking-widest text-slate-400">Student value</Label>
                          <Input
                            data-testid={`entry-student-${m.id}`}
                            value={values[m.id] ?? ""}
                            onChange={(e) => setVal(m.id, e.target.value)}
                            className="mt-1 h-9"
                          />
                        </div>
                        <div className="text-right">
                          <div className="text-[10px] uppercase tracking-widest text-slate-400">Class avg</div>
                          <div className="mt-1 font-mono tabular-nums text-sm font-semibold text-slate-500 min-w-[3rem]">
                            {classAvg[m.id] != null ? classAvg[m.id] : "—"}
                          </div>
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            ))}

            <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
              <div>
                <Label className="text-xs uppercase tracking-widest text-slate-500">Teacher sign</Label>
                <Input data-testid="teacher-sign-input" value={teacherSign} onChange={(e) => setTeacherSign(e.target.value)} className="mt-1" />
              </div>
              <div>
                <Label className="text-xs uppercase tracking-widest text-slate-500">Doctor sign</Label>
                <Input data-testid="doctor-sign-input" value={doctorSign} onChange={(e) => setDoctorSign(e.target.value)} className="mt-1" />
              </div>
            </div>
            <div className="rounded-xl border-2 border-dashed border-sky-200 bg-sky-50/40 p-4">
              <div className="flex items-center gap-2 mb-2">
                <ClipboardEdit className="w-4 h-4 text-sky-600" />
                <Label className="text-xs uppercase tracking-widest text-sky-700 font-bold">Doctor&apos;s Remarks / Prescription Note</Label>
              </div>
              <Textarea
                data-testid="remarks-input"
                value={remarks}
                onChange={(e) => setRemarks(e.target.value)}
                className="mt-1 min-h-[120px] bg-white font-serif text-[15px] leading-relaxed"
                placeholder={`e.g. Overall healthy. Advise 15 min extra outdoor play daily.\nRecommended dental cleaning next month.\nParent to monitor screen-time.`}
              />
              <div className="text-[11px] text-slate-500 mt-2 italic">Any observations, recommendations or referrals beyond the parameter list above. Visible to parent on the Report Card.</div>
            </div>
          </div>

          <DialogFooter>
            <Button data-testid="save-screening-btn" onClick={save} className="bg-sky-500 hover:bg-sky-600">
              <Save className="w-4 h-4 mr-1" /> Save Term {term}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </AppShell>
  );
}
